import { Button, Typography } from "@material-ui/core";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import debtorBlockApi from "../../../../../api/factoring/debtorBlock";
import fetchAll from "../../../../../helpers/fetchAll";
import { openSnackbar } from "../../../../../modules/ui";
import BlockAllDialog from "./BlockAllDialog";
import BlockHistoryDialog from "./BlockHistoryDialog";
import UnblockAllDialog from "./UnblockAllDialog";
import {
  bannerDetail,
  barSummary,
  canUnblockAll,
  DebtorBlockEvent,
  DebtorBlockState,
  errorMessage,
  showBanner
} from "./debtorBlock";

interface Props {
  factoringId: string;
  clientName: string;
  refreshKey: number;
  onBulkChange: () => void;
}

type OpenDialog = "block" | "unblock" | "history" | null;

const row = {
  display: "flex",
  alignItems: "center",
  gap: 16,
  padding: "14px 20px"
} as React.CSSProperties;

const FactoringBlockBar = ({
  factoringId,
  clientName,
  refreshKey,
  onBulkChange
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const [state, setState] = useState<DebtorBlockState | null>(null);
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [events, setEvents] = useState<DebtorBlockEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");

  const loadState = useCallback(async (): Promise<void> => {
    try {
      setState(await debtorBlockApi.getDebtorBlockState(factoringId));
    } catch (err) {
      dispatch(openSnackbar("error", errorMessage(err, "Could not load the factoring block status.")));
    }
  }, [factoringId, dispatch]);

  useEffect(() => {
    if (factoringId) {
      loadState();
    }
  }, [factoringId, refreshKey, loadState]);

  const askToConfirm = (dialog: "block" | "unblock"): void => {
    setError("");
    setOpenDialog(dialog);
  };

  const closeDialog = (): void => setOpenDialog(null);

  const openHistory = async (): Promise<void> => {
    setOpenDialog("history");
    setEventsLoading(true);
    setEventsError("");
    try {
      setEvents(await fetchAll<DebtorBlockEvent>(() => debtorBlockApi.getDebtorBlockEvents(factoringId)));
    } catch (err) {
      setEventsError(errorMessage(err, "Could not load the block history."));
    } finally {
      setEventsLoading(false);
    }
  };

  const runBulkAction = async (
    action: () => Promise<any>,
    success: string,
    failure: string
  ): Promise<void> => {
    setPending(true);
    setError("");
    try {
      await action();
      setOpenDialog(null);
      dispatch(openSnackbar("success", success));
      onBulkChange();
      await loadState();
    } catch (err) {
      setError(errorMessage(err, failure));
    } finally {
      setPending(false);
    }
  };

  if (!state) {
    return null;
  }

  const historyButton = (
    <Button style={{ color: "#1e88e5" }} onClick={openHistory}>
      History ({state.event_count})
    </Button>
  );

  return (
    <>
      {showBanner(state) ? (
        <div style={{ ...row, borderBottom: "1px solid #f1c7c7", background: "#fdecec" }}>
          <FontAwesomeIcon icon={faLock} color="#b71c1c" />
          <Typography component="div" style={{ display: "flex", flexDirection: "column", gap: 3, flexGrow: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#8e1414" }}>
              Factoring is blocked on all {state.total_relations} debtors for this client
            </div>
            <div style={{ fontSize: 12, color: "#5c2020" }}>
              New factored funding requests are rejected and existing ones can’t be approved.
              Non-factored and self-finance invoices are still allowed.
            </div>
            <div style={{ fontSize: 12, color: "#5c2020" }}>{bannerDetail(state)}</div>
          </Typography>
          {historyButton}
          <Button
            variant="outlined"
            style={{ borderColor: "#b71c1c", color: "#b71c1c", background: "#fff" }}
            onClick={(): void => askToConfirm("unblock")}
          >
            Unblock all
          </Button>
        </div>
      ) : (
        <div style={{ ...row, borderBottom: "1px solid #e0e0e0", background: "#fafbfc" }}>
          <FontAwesomeIcon icon={faLock} color="#616161" />
          <Typography component="div" style={{ display: "flex", flexDirection: "column", gap: 2, flexGrow: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: "#212121" }}>Factoring block</div>
            <div style={{ fontSize: 12, color: "#5f6368" }}>{barSummary(state)}</div>
          </Typography>
          {historyButton}
          <Button
            variant="outlined"
            disabled={!canUnblockAll(state)}
            onClick={(): void => askToConfirm("unblock")}
          >
            Unblock all
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "#c62828", color: "#fff" }}
            onClick={(): void => askToConfirm("block")}
          >
            Block all debtors
          </Button>
        </div>
      )}
      <BlockAllDialog
        open={openDialog === "block"}
        count={state.total_relations}
        clientName={clientName}
        pending={pending}
        error={error}
        onCancel={closeDialog}
        onConfirm={(reason, appliesToFuture): Promise<void> =>
          runBulkAction(
            () => debtorBlockApi.blockAllDebtors(factoringId, reason, appliesToFuture),
            "Factoring blocked on all debtors.",
            "Could not block the debtors. Please try again."
          )
        }
      />
      <UnblockAllDialog
        open={openDialog === "unblock"}
        count={state.total_relations}
        clientName={clientName}
        latestBlockEvent={state.latest_block_event}
        pending={pending}
        error={error}
        onCancel={closeDialog}
        onConfirm={(reason): Promise<void> =>
          runBulkAction(
            () => debtorBlockApi.unblockAllDebtors(factoringId, reason),
            "Factoring unblocked on all debtors.",
            "Could not unblock the debtors. Please try again."
          )
        }
      />
      <BlockHistoryDialog
        open={openDialog === "history"}
        clientName={clientName}
        events={events}
        loading={eventsLoading}
        error={eventsError}
        onClose={closeDialog}
      />
    </>
  );
};

export default FactoringBlockBar;
